// The pipeline: classify → spec → generate → validate → repair (at most a few rounds). Code
// orchestrates, the model only answers; every call is recorded with its token usage. Talks to
// models through the `Model` function type so tests run it with a stub, reads its prompt texts
// through PromptSources and validates through an injected function, so the same loop runs
// under Node (the CLI) and in the page (the Create tab). It never touches a filesystem: the
// caller keeps spec and source through `save`.

import { parseJson, unfence, type ChatResponse, type Model, type ReasoningEffort, type Usage } from "./openrouter.js";
import { kindPack, type KindPack } from "./kinds.js";
import { checkSpec, type KindId, type Spec } from "./spec.js";
import { classifyMessages, CLASSIFY_SCHEMA, generateMessages, repairMessages, specMessages, type Classification, type PromptSources } from "./stages.js";
import type { ValidationReport } from "./validate.js";

export interface PipelineOptions {
  prompt: string;
  /** The strong model: spec, generate, repair. */
  model: Model;
  modelId: string;
  /** The cheap model for classification; the strong one when absent. */
  fastModel?: Model;
  fastModelId?: string;
  sources: PromptSources;
  /** The validator of the host: Node writes a scratch file, the page loads a blob. */
  validate: (source: string, spec: Spec) => Promise<ValidationReport>;
  /** Called with "spec.json" and "game.js" (after every round) so the caller can keep them. */
  save?: (name: string, text: string) => void;
  /** Repair rounds after the first generation. Default 3. */
  maxRepairs?: number;
  /** Skip classification and use this kind. */
  kind?: KindId;
  report?: (line: string) => void;
}

export interface Call {
  stage: string;
  model: string;
  provider: string;
  usage: Usage;
  ms: number;
}

export interface PipelineResult {
  ok: boolean;
  /** Set when the request was declined at classification. */
  refused?: string;
  classification?: Classification;
  spec?: Spec;
  source?: string;
  rounds: { round: number; report: ValidationReport }[];
  calls: Call[];
}

export const DEFAULT_MAX_REPAIRS = 3;
/** Output budget for the generate and repair calls, thinking included. The proxy allows the same. */
export const CODE_TOKENS = 32_000;

export async function runPipeline(o: PipelineOptions): Promise<PipelineResult> {
  const say = o.report ?? (() => {});
  const save = o.save ?? (() => {});
  const calls: Call[] = [];
  const result: PipelineResult = { ok: false, rounds: [], calls };

  const ask = async (
    stage: string,
    model: Model,
    modelId: string,
    messages: Parameters<Model>[0]["messages"],
    maxTokens: number,
    schema?: Parameters<Model>[0]["schema"],
    reasoning?: ReasoningEffort,
  ) => {
    const t0 = Date.now();
    let r: ChatResponse;
    try {
      r = await model({ model: modelId, messages, maxTokens, schema, reasoning });
    } catch (err) {
      // A reply that ran past the budget still cost its tokens: count them before giving up.
      const usage = (err as { usage?: Usage }).usage;
      if (usage) calls.push({ stage, model: modelId, provider: "", usage, ms: Date.now() - t0 });
      throw err;
    }
    const call = { stage, model: r.model || modelId, provider: r.provider, usage: r.usage, ms: Date.now() - t0 };
    calls.push(call);
    const cache = call.usage.cached ? `${call.usage.cached} from cache` : call.usage.cacheWrite ? `${call.usage.cacheWrite} written to cache` : "no cache";
    const thinking = call.usage.reasoning ? ` (${call.usage.reasoning} of them thinking)` : "";
    say(`${stage}: ${call.usage.prompt} in (${cache}), ${call.usage.completion} out${thinking}, ${(call.ms / 1000).toFixed(1)} s${call.provider ? ` via ${call.provider}` : ""}`);
    return r.text;
  };

  /**
   * A code-writing call. A reply that runs past the budget has been mostly thinking (Cave
   * Dive, 2026-10-16: 12,399 of 16,000 tokens, the file a third written), so the one retry
   * asks for low thinking effort rather than more room.
   */
  const askCode = async (stage: string, messages: Parameters<Model>[0]["messages"]) => {
    try {
      return unfence(await ask(stage, o.model, o.modelId, messages, CODE_TOKENS));
    } catch (err) {
      if ((err as { code?: string }).code !== "length") throw err;
      say(`${stage}: ${(err as Error).message}`);
      say(`${stage}: asking again with low thinking effort`);
      return unfence(await ask(`${stage}-retry`, o.model, o.modelId, messages, CODE_TOKENS, undefined, "low"));
    }
  };

  // 1. Classify, unless the kind was given.
  let pack: KindPack | undefined;
  let title = "";
  if (o.kind) {
    pack = kindPack(o.kind);
    if (!pack) throw new Error(`unknown kind "${o.kind}"`);
  } else {
    const fast = o.fastModel ?? o.model;
    const fastId = o.fastModelId ?? o.modelId;
    // The answer is six short fields, but the fast model has been seen to spend over 400
    // tokens on it (2026-10-15), so the budget is generous: it is the cheap call.
    const c = parseJson<Classification>(await ask("classify", fast, fastId, classifyMessages(o.prompt), 1000, CLASSIFY_SCHEMA));
    result.classification = c;
    if (!c.ok) {
      result.refused = c.reason || "That is not something this can make.";
      return result;
    }
    pack = kindPack(c.kind) ?? kindPack(c.kind2);
    if (!pack) throw new Error(`the classifier answered with an unknown kind "${c.kind}"`);
    title = c.title;
    say(`kind: ${pack.id} (${Math.round(c.confidence * 100)}%, runner-up ${c.kind2}), title: ${c.title}`);
  }

  // 2. Spec, with one retry if it does not check out.
  let spec: Spec | undefined;
  // No response schema here on purpose: the spec and generate calls must share one cached prefix (see specMessages).
  let specText = await ask("spec", o.model, o.modelId, specMessages(pack, o.prompt, title, o.sources), 4000);
  for (let attempt = 0; attempt < 2 && !spec; attempt++) {
    const candidate = parseJson<Spec>(specText);
    const problems = checkSpec(candidate);
    if (problems.length === 0) spec = candidate;
    else if (attempt === 0) {
      say(`spec: ${problems.length} problem(s), asking again: ${problems.join("; ")}`);
      const msgs = specMessages(pack, o.prompt, title, o.sources);
      msgs.push({ role: "assistant", content: specText });
      msgs.push({ role: "user", content: `The spec has problems; return the whole spec again with them fixed:\n- ${problems.join("\n- ")}` });
      specText = await ask("spec-retry", o.model, o.modelId, msgs, 4000);
    } else throw new Error(`the spec still has problems: ${problems.join("; ")}`);
  }
  if (!spec) throw new Error("no spec");
  if (spec.kind !== pack.id) {
    const other = kindPack(spec.kind);
    if (other) {
      say(`spec chose kind ${spec.kind} over ${pack.id}; following the spec`);
      pack = other;
    }
  }
  result.spec = spec;
  save("spec.json", JSON.stringify(spec, null, 2));
  say(`spec: "${spec.title}" (${spec.slug}), ${spec.entities.length} entities, look ${spec.look.style}, ${spec.rules.length} rules`);

  // 3. Generate, then validate and repair. The budget covers the model's thinking as well as
  // the file: a six-entity game spent 12,399 of 16,000 tokens thinking before the file was
  // a third written (2026-10-16). Unused budget costs nothing.
  let source = await askCode("generate", generateMessages(pack, spec, o.sources));
  const maxRepairs = o.maxRepairs ?? DEFAULT_MAX_REPAIRS;
  for (let round = 0; ; round++) {
    save("game.js", source);
    const report = await o.validate(source, spec);
    result.rounds.push({ round, report });
    const errors = report.findings.filter((f) => f.level === "error");
    const warns = report.findings.filter((f) => f.level === "warn");
    say(
      `validate round ${round}: ${report.ok ? "ok" : `${errors.length} error(s)`}${warns.length ? `, ${warns.length} warning(s)` : ""}` +
        (report.measured ? ` · ${report.measured.stepAvgMs.toFixed(2)} ms/tick, ${(report.measured.stateBytes / 1024).toFixed(0)} KB state, ${(report.measured.snapshotBytes / 1024).toFixed(1)} KB snapshot` : ""),
    );
    for (const f of report.findings) say(`  [${f.level}] ${f.code}: ${f.message}`);
    if (report.ok) {
      result.ok = true;
      break;
    }
    if (round >= maxRepairs) {
      say(`giving up after ${maxRepairs} repair round(s); the last game.js and its findings stand`);
      break;
    }
    const before = source;
    source = await askCode(`repair-${round + 1}`, repairMessages(pack, spec, source, report.findings, round + 1, o.sources));
    const shrink = 1 - source.length / Math.max(1, before.length);
    if (shrink > 0.4) say(`  note: the repair removed ${Math.round(shrink * 100)}% of the file; check that no rule was dropped`);
  }
  result.source = source;
  return result;
}

/** Totals across every call. */
export function totals(calls: Call[]): Usage & { ms: number } {
  return calls.reduce(
    (t, c) => ({
      prompt: t.prompt + c.usage.prompt,
      completion: t.completion + c.usage.completion,
      cached: t.cached + c.usage.cached,
      cacheWrite: t.cacheWrite + c.usage.cacheWrite,
      reasoning: t.reasoning + c.usage.reasoning,
      cost: t.cost + c.usage.cost,
      ms: t.ms + c.ms,
    }),
    { prompt: 0, completion: 0, cached: 0, cacheWrite: 0, reasoning: 0, cost: 0, ms: 0 },
  );
}
